import {undef} from './undef';
import {getNoUndefValuesArray} from './array';
import {getQuantityLabel} from './string';
import {allowFieldAutoSubmit, allowInputFieldErrorHighlighting} from './field';
import {runHPGuestPickerCTASearchExperiment, runGuestPickerCTASearchExperiment} from './experiments';

// eslint-disable-next-line complexity
function getGuestsFieldInputLabel(guestsTranslations, guestsValues, showBedroomsOption) {
    const {
        guestLabel
    } = guestsTranslations;

    if (guestLabel) {
        return guestLabel;
    }

    const {
        adultsCount,
        childrenCount
    } = guestsValues;

    const guestsCount = adultsCount + childrenCount;

    const {
        moreText,
        separatorText,
        guestText,
        guestsText,
        bedroomsInputText,
        petsText,
        bedroomsLabel
    } = guestsTranslations;

    const {
        bedroomsCount,
        petIncluded
    } = guestsValues;

    return getNoUndefValuesArray([
        guestsCount ? getQuantityLabel({
            count: guestsCount,
            singular: guestText,
            plural: guestsText
        }) : undef,
        bedroomsCount && showBedroomsOption
            ?
            getQuantityLabel({
                label: bedroomsLabel,
                count: bedroomsCount,
                more: moreText,
                plural: bedroomsInputText
            }) : undef,
        petIncluded ? petsText : undef
    ]).join(separatorText);
}

function getGuestsFieldAdultsInputLabel(guestsTranslations, guestsValues) {
    const {
        adultText,
        adultsText,
        adultsLabel
    } = guestsTranslations;

    const {
        adultsCount
    } = guestsValues;

    return getQuantityLabel({
        label: adultsLabel,
        count: adultsCount,
        singular: adultText,
        plural: adultsText
    });
}

function getGuestsFieldChildrenInputLabel(guestsTranslations, guestsValues) {
    const {
        childText,
        childrenText,
        childrenLabel
    } = guestsTranslations;

    const {
        childrenCount
    } = guestsValues;

    return getQuantityLabel({
        label: childrenLabel,
        count: childrenCount,
        singular: childText,
        plural: childrenText
    });
}

function getGuestsFieldBedroomsInputLabel(guestsTranslations, guestsValues) {
    const {
        moreText,
        bedroomsSelectorText,
        bedroomsAnySelectorText,
        bedroomsLabel
    } = guestsTranslations;

    const {
        bedroomsCount
    } = guestsValues;

    return getQuantityLabel({
        label: bedroomsLabel,
        count: bedroomsCount,
        any: bedroomsAnySelectorText,
        more: moreText,
        plural: bedroomsSelectorText
    });
}

function getGuestsFieldAgeSelectTitle(guestsTranslations, guestsValues) {
    const {
        ageSelectTitle,
        ageSelectTitleSingular
    } = guestsTranslations;

    const {
        childrenCount
    } = guestsValues;

    return childrenCount > 1 ? ageSelectTitle : ageSelectTitleSingular;
}

function hasEmptyGuestsCount(guestsCount) {
    return !guestsCount;
}

// eslint-disable-next-line no-unused-vars
function hasEmptyGuests(adultsCount, childrenCount, bedroomsCount, petIncluded) {
    return hasEmptyGuestsCount(adultsCount);
}

function hasEmptyGuestsValues({adultsCount, childrenCount, bedroomsCount, petIncluded}) {
    return hasEmptyGuests(adultsCount, childrenCount, bedroomsCount, petIncluded);
}

function hasFilledGuestsCount(guestsCount) {
    return !hasEmptyGuestsCount(guestsCount);
}

/* istanbul ignore next */
// eslint-disable-next-line no-unused-vars
function hasFilledGuests(adultsCount, childrenCount, bedroomsCount, petIncluded) {
    return hasFilledGuestsCount(adultsCount);
}

/* istanbul ignore next */
function hasFilledGuestsValues({adultsCount, childrenCount, bedroomsCount, petIncluded}) {
    return hasFilledGuests(adultsCount, childrenCount, bedroomsCount, petIncluded);
}

function hasValidGuestsCount(guestsCount) {
    return hasFilledGuestsCount(guestsCount);
}

// eslint-disable-next-line no-unused-vars
function hasValidGuests(adultsCount, childrenCount, bedroomsCount, petIncluded) {
    return hasValidGuestsCount(adultsCount);
}

function hasValidGuestsValues({adultsCount, childrenCount, bedroomsCount, petIncluded}) {
    return hasValidGuests(adultsCount, childrenCount, bedroomsCount, petIncluded);
}

function shouldChildrenAgesCookieUpdate(actionLocation) {
    return actionLocation === 'search';
}

function shouldGuestsFieldAutoSubmitOnClose(mediaSettings, formOptions, guestsOptions, abacusStore, systemOptions, latestDestinationValues, latestDatesValues) {
    const {
        isMobile
    } = mediaSettings;

    return (
        isMobile && allowFieldAutoSubmit(formOptions, guestsOptions) ||
        runGuestPickerCTASearchExperiment({shouldLogExperiment: true}, abacusStore, latestDestinationValues, latestDatesValues) ||
        runHPGuestPickerCTASearchExperiment({shouldLogExperiment: true}, abacusStore, systemOptions, latestDestinationValues, latestDatesValues)
    );
}

function getAddedChildAgeData(ageOptions) {
    const {minAge, useUndefAges, undefAgesValue} = ageOptions;
    if (typeof minAge !== 'undefined') {
        return {
            value: minAge,
            label: `${minAge}`,
        };
    }
    if (useUndefAges && undefAgesValue) {
        return {
            ...undefAgesValue,
        };
    }
    return {
        value: 0,
        label: '0',
    };
}

function setChildrenAgeSelectorData(childrenCount, props) {
    const {prevValue, nextValue} = childrenCount;
    let ageData = props.ageData;
    ageData =
        prevValue < nextValue
            ? [
                ...ageData,
                getAddedChildAgeData(props.ageOptions)
            ]
            : ageData.slice(0, nextValue);
    return ageData;
}

function showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues) {
    const {
        isMobile
    } = mediaSettings;

    return !isMobile && (
        allowInputFieldErrorHighlighting(formOptions, guestsOptions) && !hasValidGuestsValues(latestGuestsValues)
    );
}

function showGuestsInputIcon(formOptions, guestsOptions) {
    const {
        showIcons
    } = formOptions;

    const {
        showInputIcon
    } = guestsOptions;

    return (
        showInputIcon ||
        showIcons && showInputIcon === undef
    );
}

export {
    getAddedChildAgeData,
    getGuestsFieldInputLabel,
    getGuestsFieldAdultsInputLabel,
    getGuestsFieldAgeSelectTitle,
    getGuestsFieldChildrenInputLabel,
    getGuestsFieldBedroomsInputLabel,
    hasEmptyGuests,
    hasEmptyGuestsCount,
    hasEmptyGuestsValues,
    hasFilledGuests,
    hasFilledGuestsCount,
    hasFilledGuestsValues,
    hasValidGuests,
    hasValidGuestsCount,
    hasValidGuestsValues,
    shouldChildrenAgesCookieUpdate,
    shouldGuestsFieldAutoSubmitOnClose,
    setChildrenAgeSelectorData,
    showGuestsInputFieldError,
    showGuestsInputIcon
};
